import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from 'convex/react'
import { useTranslation } from 'react-i18next'
import { api } from '../../convex/_generated/api'
import VideoCard from '../components/VideoCard'
import VideoCardSkeleton from '../components/VideoCardSkeleton'
import ModelCard from '../components/ModelCard'
import AuthModal from '../components/AuthModal'

// Types for video and model data
interface VideoData {
  _id: string
  id?: string
  title: string
  duration: number
  views: number
  thumbnailUrl?: string
  quality?: string
  likes?: number
  price?: number
  muxPlaybackId?: string
  cloudinaryThumbnailId?: string
  accessLevel?: string
}

interface ModelData {
  _id: string
  id?: string
  stageName: string
  userName?: string
  name?: string
  profileImage?: string
  videoCount?: number
  featured?: boolean
}

// Sample data for demo mode (when database is empty)
const sampleVideos = [
  { _id: '1', title: 'Exclusive Premium Content - Episode 1', duration: 754, views: 125000, muxPlaybackId: 'demo', cloudinaryThumbnailId: '', accessLevel: 'free' },
  { _id: '2', title: 'Behind The Scenes - Model Showcase', duration: 525, views: 89000, muxPlaybackId: 'demo', cloudinaryThumbnailId: '', accessLevel: 'free' },
  { _id: '3', title: 'VIP Special - Private Collection', duration: 922, views: 203000, muxPlaybackId: 'demo', cloudinaryThumbnailId: '', accessLevel: 'premium' },
  { _id: '4', title: 'New Model Introduction - Mystique', duration: 611, views: 67000, muxPlaybackId: 'demo', cloudinaryThumbnailId: '', accessLevel: 'free' },
  { _id: '5', title: 'Premium Fantasy Series - Part 1', duration: 1110, views: 312000, muxPlaybackId: 'demo', cloudinaryThumbnailId: '', accessLevel: 'premium' },
  { _id: '6', title: 'Exclusive Interview & More', duration: 1335, views: 156000, muxPlaybackId: 'demo', cloudinaryThumbnailId: '', accessLevel: 'basic' },
  { _id: '7', title: 'Late Night Special - Full HD', duration: 848, views: 98000, muxPlaybackId: 'demo', cloudinaryThumbnailId: '', accessLevel: 'free' },
  { _id: '8', title: 'Members Only - Week 12', duration: 1500, views: 445000, muxPlaybackId: 'demo', cloudinaryThumbnailId: '', accessLevel: 'vip' },
]

const sampleModels = [
  { _id: '1', stageName: 'Mystique', videoCount: 45, featured: true },
  { _id: '2', stageName: 'Shadow', videoCount: 38, featured: true },
  { _id: '3', stageName: 'Phantom', videoCount: 52, featured: true },
  { _id: '4', stageName: 'Enigma', videoCount: 29, featured: true },
  { _id: '5', stageName: 'Mirage', videoCount: 61, featured: true },
  { _id: '6', stageName: 'Aurora', videoCount: 33, featured: true },
]

const categories = [
  'all', 'new', 'trending', 'popular', 'featured', 'vip', 'premium', 'exclusive', 'hd', '4k'
]

export default function Home() {
  const { t } = useTranslation()
  const [activeCategory, setActiveCategory] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [showAuthModal, setShowAuthModal] = useState(false)

  // Fetch data from Convex
  const trendingVideos = useQuery(api.videos.getTrendingVideos, { limit: 12 })
  const latestVideos = useQuery(api.videos.getLatestVideos, { limit: 8 })
  const freePreviewVideos = useQuery(api.videos.getFreePreviewVideos, { limit: 4 })
  const featuredModels = useQuery(api.models.getFeaturedModels, { limit: 6 })

  // Use Convex data with sample fallback for demo mode
  const displayVideos = (trendingVideos && trendingVideos.length > 0) ? trendingVideos : sampleVideos
  const displayLatestVideos = (latestVideos && latestVideos.length > 0) ? latestVideos : sampleVideos.slice(0, 4)
  const displayFreeVideos = (freePreviewVideos && freePreviewVideos.length > 0) ? freePreviewVideos : sampleVideos.filter(v => v.accessLevel === 'free')
  const displayModels = (featuredModels && featuredModels.length > 0) ? featuredModels : sampleModels

  // Show loading state only when still fetching
  const isLoading = trendingVideos === undefined

  return (
    <>
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        initialMode="signup"
      />

      <main className="main-content">
        {/* Hero Banner */}
        <section className="hero-banner">
          <h1>{t('hero.title')}</h1>
          <p>{t('hero.subtitle')}</p>
          <button onClick={() => setShowAuthModal(true)} className="hero-btn">
            {t('hero.cta')}
          </button>
        </section>

        {/* Category Tags */}
        <div className="category-tags">
          {categories.map((category) => (
            <button
              key={category}
              className={`tag ${activeCategory === category ? 'active' : ''}`}
              onClick={() => setActiveCategory(category)}
            >
              {t(`category.${category}`)}
            </button>
          ))}
        </div>

        {/* Trending Videos Section */}
        <section>
          <div className="section-header">
            <h2 className="section-title">🔥 {t('section.trending')}</h2>
            <Link to="/categories" className="view-all">{t('common.view_all')} →</Link>
          </div>

          <div className="video-grid">
            {isLoading ? (
              <VideoCardSkeleton count={8} />
            ) : displayVideos.length > 0 ? (
              (displayVideos as VideoData[]).map((video, idx) => (
                <VideoCard
                  key={video._id || video.id || idx}
                  id={video._id || video.id || String(idx)}
                  title={video.title}
                  thumbnailUrl={video.thumbnailUrl}
                  duration={video.duration}
                  views={video.views}
                  quality={video.quality || 'HD'}
                  likes={video.likes}
                  isPremium={video.accessLevel === 'premium' || video.accessLevel === 'vip'}
                  index={idx}
                />
              ))
            ) : (
              <p className="empty-state">{t('home.no_trending')}</p>
            )}
          </div>
        </section>

        {/* Featured Models Section */}
        <section>
          <div className="section-header">
            <h2 className="section-title">⭐ {t('section.featured_models')}</h2>
            <Link to="/models" className="view-all">{t('common.view_all')} →</Link>
          </div>

          <div className="model-grid">
            {isLoading ? (
              <p className="loading-state">{t('home.loading_models')}</p>
            ) : displayModels.length > 0 ? (
              (displayModels as ModelData[]).map((model, idx) => (
                <ModelCard
                  key={model._id || model.id || idx}
                  id={model._id || model.id || String(idx)}
                  name={model.userName || model.name || 'Anonymous'}
                  stageName={model.stageName}
                  profileImage={model.profileImage}
                  videoCount={model.videoCount || ((idx + 1) * 7 + 15)}
                  index={idx}
                />
              ))
            ) : (
              <p className="empty-state">{t('home.no_models')}</p>
            )}
          </div>
        </section>

        {/* Latest Videos Section */}
        <section>
          <div className="section-header">
            <h2 className="section-title">📺 {t('section.latest')}</h2>
            <Link to="/categories" className="view-all">{t('common.view_all')} →</Link>
          </div>

          <div className="video-grid">
            {latestVideos === undefined ? (
              <VideoCardSkeleton count={4} />
            ) : displayLatestVideos.length > 0 ? (
              (displayLatestVideos as VideoData[]).map((video, idx) => (
                <VideoCard
                  key={`latest-${video._id || video.id || idx}`}
                  id={video._id || video.id || String(idx)}
                  title={video.title}
                  thumbnailUrl={video.thumbnailUrl}
                  duration={video.duration}
                  views={video.views}
                  quality={video.quality || 'HD'}
                  index={idx}
                />
              ))
            ) : (
              <p className="empty-state">{t('home.no_videos')}</p>
            )}
          </div>
        </section>

        {/* Free Preview Section */}
        {displayFreeVideos.length > 0 && (
          <section>
            <div className="section-header">
              <h2 className="section-title">🎁 {t('section.free')}</h2>
              <Link to="/categories?filter=free" className="view-all">{t('common.view_all')} →</Link>
            </div>

            <div className="video-grid">
              {(displayFreeVideos as VideoData[]).map((video, idx) => (
                <VideoCard
                  key={`free-${video._id || video.id || idx}`}
                  id={video._id || video.id || String(idx)}
                  title={video.title}
                  thumbnailUrl={video.thumbnailUrl}
                  duration={video.duration}
                  views={video.views}
                  quality={video.quality || 'HD'}
                  index={idx}
                />
              ))}
            </div>
          </section>
        )}

        {/* Pagination */}
        <nav className="pagination">
          <button className="page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
            ← {t('common.prev')}
          </button>
          {[1, 2, 3, 4, 5].map((page) => (
            <button
              key={page}
              className={`page-btn ${currentPage === page ? 'active' : ''}`}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}
          <button className="page-btn" onClick={() => setCurrentPage(p => p + 1)}>
            {t('common.next')} →
          </button>
        </nav>
      </main>
    </>
  )
}
